import type { RateCardDraftContext } from "./rateCardPreview";
import { sanitizeMarkupTiers } from "./sanitizeMarkupTiers";

export type RateCardLint = {
    level: "warning" | "error";
    message: string;
    section?: string;
};

function num(v: string | null | undefined): number | null {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

export function validateRateCard(ctx: RateCardDraftContext): RateCardLint[] {
    const lints: RateCardLint[] = [];
    const isTM = (ctx.pricing.billing_mode ?? "flat_rate") === "time_and_materials";

    if (ctx.trades.length === 0 && isTM) {
        lints.push({
            level: "warning",
            section: "Services",
            message: "No services selected — pick at least one trade this customer receives.",
        });
    }

    if (isTM && ctx.trades.length > 0 && num(ctx.pricing.hourly_labor_rate) == null) {
        lints.push({
            level: "warning",
            section: "Labor",
            message: "Contract mode usually needs an hourly labor rate.",
        });
    }

    if (isTM && ctx.trades.includes("backflow") && num(ctx.pricing.test_fee_flat) == null) {
        lints.push({
            level: "warning",
            section: "Testing",
            message: "Backflow is selected but no standard test fee is set.",
        });
    }

    if (
        isTM &&
        ctx.trades.includes("backflow") &&
        ctx.splitFirelineMainFee &&
        num(ctx.pricing.test_fee_fireline_main) == null
    ) {
        lints.push({
            level: "warning",
            section: "Testing",
            message: "Split fireline-main fee is enabled but no fireline main rate is set.",
        });
    }

    if (isTM && ctx.trades.includes("fire_ext") && !ctx.extPricing) {
        lints.push({
            level: "warning",
            section: "Extinguishers",
            message: "Fire extinguisher service is selected but pricing is not configured.",
        });
    }

    const mode = ctx.pricing.materials_pricing_mode;
    if (isTM && mode === "at_cost_plus" && num(ctx.pricing.materials_markup_pct) == null) {
        lints.push({
            level: "warning",
            section: "Materials",
            message: "Materials mode is cost + markup but markup % is blank.",
        });
    }
    if (isTM && mode === "fixed_discount_off_list" && num(ctx.pricing.materials_discount_pct) == null) {
        lints.push({
            level: "warning",
            section: "Materials",
            message: "Materials mode is discount off list but discount % is blank.",
        });
    }

    if (isTM && mode === "tiered_markup") {
        const tiers = sanitizeMarkupTiers(ctx.tiers);
        if (tiers.length === 0) {
            lints.push({
                level: "error",
                section: "Materials",
                message: "Tiered markup mode requires at least one tier band.",
            });
        } else {
            const sorted = [...tiers].sort((a, b) => (num(a.min_amount) ?? 0) - (num(b.min_amount) ?? 0));
            const firstMin = num(sorted[0]?.min_amount);
            if (firstMin != null && firstMin !== 0) {
                lints.push({
                    level: "warning",
                    section: "Materials",
                    message: "First tier should usually start at $0.",
                });
            }
            for (let i = 0; i < sorted.length - 1; i++) {
                const curMax = num(sorted[i]?.max_amount);
                const nextMin = num(sorted[i + 1]?.min_amount);
                if (curMax != null && nextMin != null && nextMin > curMax + 0.01) {
                    lints.push({
                        level: "error",
                        section: "Materials",
                        message: `Gap between tier ${i + 1} max ($${curMax}) and tier ${i + 2} min ($${nextMin}).`,
                    });
                }
                if (curMax != null && nextMin != null && nextMin <= curMax) {
                    lints.push({
                        level: "error",
                        section: "Materials",
                        message: `Overlap between tier ${i + 1} and tier ${i + 2}.`,
                    });
                }
            }
            const last = sorted[sorted.length - 1];
            if (last?.max_amount != null && last.max_amount !== "") {
                lints.push({
                    level: "warning",
                    section: "Materials",
                    message: "Last tier usually has no max (open-ended).",
                });
            }
        }
    }

    return lints;
}
